import { useAuth0 } from '@auth0/auth0-react'
import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'

import { useIdentity } from '~/shell/auth/useIdentity'

/**
 * Attaches the authenticated session's identity to Datadog RUM.
 *
 * Lives inside the React tree (it needs `useAuth0`) and renders nothing,
 * mirroring `AuthTokenBridge`. RUM is initialized before React mounts with only
 * static attributes (service, env, version); this bridge fills in the
 * per-session pieces once Auth0 resolves — the Auth0 subject as the RUM user id
 * and name, and the active customer as global context.
 *
 * The customer and admin-ness flow through the `useIdentity` seam. This bridge mounts outside the
 * guarded tree, so it renders before the session's claims are resolved and reads an unresolved identity
 * first; the seam is subscribed, so the attributes are corrected in the render the resolve triggers
 * rather than staying wrong for the session.
 *
 * Every call is a safe no-op when RUM was never initialized (blank tokens, dev,
 * or the E2E bypass), so this bridge needs no extra guard.
 */
export const DatadogIdentityBridge = () => {
  const { user } = useAuth0()
  const { isAdmin, customerClaim } = useIdentity()

  useEffect(() => {
    if (user?.sub) {
      datadogRum.setUser({ id: user.sub, name: user.name })
    }
  }, [user?.sub, user?.name])

  useEffect(() => {
    datadogRum.setGlobalContextProperty('customer', customerClaim ?? undefined)
    datadogRum.setGlobalContextProperty('is_admin', isAdmin)
  }, [customerClaim, isAdmin])

  return null
}
